import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Status = 'PENDING' | 'APPROVED' | 'REJECTED';
type SmsType =
  | 'CARD_OTP'
  | 'BANK_TRANSFER_OTP'
  | 'BENEFICIARY_ADDITION_OTP'
  | 'LOGIN_OTP'
  | 'DEBIT_CONFIRMATION'
  | 'CREDIT_CONFIRMATION'
  | 'ADVISORY'
  | 'PROMOTIONAL'
  | 'PERSONAL'
  | 'UNKNOWN';

interface Template {
  id: string;
  skeletonText: string;
  senderPattern: string | null;
  status: Status;
  smsType: SmsType | null;
  sampleCount: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

interface Sample {
  id: string;
  senderId: string;
  smsBody: string;
  receivedAt: string;
  routingAction: string | null;
}

const SMS_TYPE_OPTIONS: { value: SmsType; label: string; emphasis?: boolean }[] = [
  { value: 'CARD_OTP', label: 'Card OTP', emphasis: true },
  { value: 'BANK_TRANSFER_OTP', label: 'Bank transfer OTP', emphasis: true },
  { value: 'BENEFICIARY_ADDITION_OTP', label: 'Beneficiary-add OTP', emphasis: true },
  { value: 'LOGIN_OTP', label: 'Login OTP', emphasis: true },
  { value: 'DEBIT_CONFIRMATION', label: 'Debit confirmation' },
  { value: 'CREDIT_CONFIRMATION', label: 'Credit confirmation' },
  { value: 'ADVISORY', label: 'Advisory / notice' },
  { value: 'PROMOTIONAL', label: 'Promotional / junk' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'UNKNOWN', label: 'Unknown / leave for later' },
];

const FILTERS: Array<{ key: string; label: string; status: Status; minSize?: number }> = [
  { key: 'pending-mega', label: 'Pending · ≥100 samples', status: 'PENDING', minSize: 100 },
  { key: 'pending-large', label: 'Pending · ≥20 samples', status: 'PENDING', minSize: 20 },
  { key: 'pending-med', label: 'Pending · ≥5 samples', status: 'PENDING', minSize: 5 },
  { key: 'pending-all', label: 'Pending · all', status: 'PENDING' },
  { key: 'approved', label: 'Approved', status: 'APPROVED' },
  { key: 'rejected', label: 'Rejected', status: 'REJECTED' },
];

export function SmsTemplatesPage() {
  const [filterKey, setFilterKey] = useState<string>('pending-mega');
  const [relevantOnly, setRelevantOnly] = useState<boolean>(true);
  const filter = useMemo(() => FILTERS.find((f) => f.key === filterKey)!, [filterKey]);
  const qc = useQueryClient();

  const stats = useQuery<Record<Status, { templates: number; samples: number }>>({
    queryKey: ['sms-templates', 'stats'],
    queryFn: () => api('/api/sms-templates/stats'),
  });

  const coverage = useQuery<{
    matched_approved: number;
    matched_pending: number;
    matched_rejected: number;
    unmatched: number;
    no_hash: number;
    total: number;
  }>({
    queryKey: ['sms-templates', 'coverage'],
    queryFn: () => api('/api/sms-templates/coverage'),
    refetchInterval: 60_000,
  });

  const list = useQuery<{ items: Template[]; hasMore: boolean }>({
    queryKey: ['sms-templates', filterKey, relevantOnly],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('status', filter.status);
      if (filter.minSize) qs.set('minSize', String(filter.minSize));
      qs.set('relevant', relevantOnly ? 'true' : 'false');
      qs.set('limit', '100');
      return api(`/api/sms-templates?${qs.toString()}`);
    },
  });

  return (
    <div className="page-canvas">
      <div className="mx-auto max-w-7xl space-y-4 p-5">
        {coverage.data && coverage.data.total > 0 && (
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="flex items-baseline justify-between gap-3">
                <span>Last 24h coverage</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {coverage.data.total.toLocaleString()} SMSes ingested
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CoverageBar data={coverage.data} />
            </CardContent>
          </Card>
        )}

        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="flex items-baseline justify-between gap-3">
              <span>SMS templates</span>
              {stats.data && (
                <span className="text-xs font-normal text-muted-foreground">
                  {stats.data.APPROVED.samples.toLocaleString()} samples covered ·{' '}
                  {stats.data.PENDING.templates.toLocaleString()} templates awaiting label
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilterKey(f.key)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs transition',
                    filterKey === f.key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background hover:bg-muted',
                  )}
                >
                  {f.label}
                </button>
              ))}
              <label className="ml-auto flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={relevantOnly}
                  onChange={(e) => setRelevantOnly(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                OTP / debit / credit only
              </label>
            </div>

            {list.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
              </div>
            ) : list.data?.items.length === 0 ? (
              <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                Nothing in this bucket. Pick another filter on the left.
              </div>
            ) : (
              <div className="space-y-2">
                {list.data?.items.map((t) => (
                  <TemplateRow
                    key={t.id}
                    template={t}
                    onClassified={() => {
                      qc.invalidateQueries({ queryKey: ['sms-templates'] });
                    }}
                  />
                ))}
                {list.data?.hasMore && (
                  <div className="text-xs text-muted-foreground">
                    Showing first 100 — narrow the filter to see more.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CoverageBar({
  data,
}: {
  data: {
    matched_approved: number;
    matched_pending: number;
    matched_rejected: number;
    unmatched: number;
    no_hash: number;
    total: number;
  };
}) {
  const pct = (n: number) => (data.total === 0 ? 0 : (n / data.total) * 100);
  const segments: Array<{ key: string; label: string; count: number; color: string }> = [
    { key: 'matched_approved', label: 'Routed via template', count: data.matched_approved, color: 'bg-emerald-500' },
    { key: 'matched_pending', label: 'Template known, awaiting label', count: data.matched_pending, color: 'bg-amber-400' },
    { key: 'matched_rejected', label: 'Suppressed (junk template)', count: data.matched_rejected, color: 'bg-zinc-400' },
    { key: 'unmatched', label: 'New template (regex fallback)', count: data.unmatched, color: 'bg-rose-400' },
    { key: 'no_hash', label: 'Pre-resolver (legacy)', count: data.no_hash, color: 'bg-zinc-200' },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {segments.map((s) => (
          <div
            key={s.key}
            className={cn('h-full', s.color)}
            style={{ width: `${pct(s.count)}%` }}
            title={`${s.label} — ${s.count.toLocaleString()} (${pct(s.count).toFixed(1)}%)`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
        {segments.map((s) => (
          <div key={s.key} className="flex items-baseline gap-2">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', s.color)} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="ml-auto tabular-nums">
              {s.count.toLocaleString()}{' '}
              <span className="text-muted-foreground">({pct(s.count).toFixed(0)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateRow({
  template,
  onClassified,
}: {
  template: Template;
  onClassified: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [picked, setPicked] = useState<SmsType>(template.smsType ?? 'UNKNOWN');

  const samples = useQuery<{ samples: Sample[] }>({
    queryKey: ['sms-templates', template.id, 'samples'],
    queryFn: () => api(`/api/sms-templates/${template.id}/samples`),
    enabled: expanded,
  });

  const classify = useMutation({
    mutationFn: (vars: { smsType: SmsType; status: 'APPROVED' | 'REJECTED' }) =>
      api(`/api/sms-templates/${template.id}/classify`, {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => onClassified(),
  });

  const isOtpLike =
    template.smsType === 'CARD_OTP' ||
    template.smsType === 'BANK_TRANSFER_OTP' ||
    template.smsType === 'LOGIN_OTP' ||
    template.smsType === 'BENEFICIARY_ADDITION_OTP';

  return (
    <div
      className={cn(
        'rounded-md border bg-card p-3',
        isOtpLike ? 'border-emerald-300/60' : 'border-border',
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-start gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {template.smsType ?? 'unknown'}
              </span>
              <span className="text-[10px] text-muted-foreground">
                · {template.sampleCount.toLocaleString()} samples
              </span>
              {template.senderPattern && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  · {template.senderPattern.slice(0, 60)}
                </span>
              )}
            </div>
            <div className="mt-1 break-words font-mono text-xs text-foreground">
              {template.skeletonText}
            </div>
          </div>
        </button>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <select
            value={picked}
            onChange={(e) => setPicked(e.target.value as SmsType)}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            {SMS_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={() => classify.mutate({ smsType: picked, status: 'APPROVED' })}
            disabled={classify.isPending}
          >
            {classify.isPending ? '…' : 'Approve'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => classify.mutate({ smsType: 'PROMOTIONAL', status: 'REJECTED' })}
            disabled={classify.isPending}
          >
            {classify.isPending ? '…' : 'Reject'}
          </Button>
        </div>
      </div>
      {classify.isError && (
        <div className="mt-2 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          Could not save: {(classify.error as Error)?.message ?? 'unknown error'}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          {samples.isLoading ? (
            <div className="text-xs text-muted-foreground">Loading samples…</div>
          ) : samples.data?.samples.length === 0 ? (
            <div className="text-xs text-muted-foreground">No samples on file.</div>
          ) : (
            samples.data?.samples.map((s) => (
              <div key={s.id} className="rounded bg-muted/40 p-2">
                <div className="flex items-baseline gap-2 text-[10px] text-muted-foreground">
                  <span className="font-mono">{s.senderId}</span>
                  <span>· {new Date(s.receivedAt).toLocaleString()}</span>
                  {s.routingAction && <span>· {s.routingAction}</span>}
                </div>
                <div className="mt-1 break-words font-mono text-xs">{s.smsBody}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
